import { rest } from "msw";
import type { RestHandler } from "msw";
import config from "../../config";
import { profileTypes } from "../../modules/profiles/profile.constant";
import type { Addresses } from "../../modules/profiles/types/address.type";
import type {
  AmlScreenMonitoringDetails,
  AmlScreenMonitoringScheduleDetails,
  AmlSearchByIdDetails,
} from "../../modules/profiles/types/amlScreen.type";
import type {
  Attachments,
  KeyPartiesType,
  MockGetNotesResponse,
  Profile,
  UboDetails,
} from "../../modules/profiles/types/profile.type";
import type {
  ProfileDetails,
  UpdateProfileDetailsPayload,
} from "../../modules/profiles/types/profileDetails.type";
import type { User } from "../../shared/types/user.type";
import { createUrl } from "../protect-api";
import { apiVersion } from "../../shared/constants/common.constant";
import type { ReportsListItem } from "../../modules/generate-reports/report.types";
import { ReportsStatus } from "../../modules/generate-reports/report.types";

export const mockGetProfilesCount = (
  countsByTeamId: Record<string, number> = {}
): RestHandler =>
  rest.get(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles`,
    async (request, res, ctx) => {
      const teamId = request.headers.get("X-Team-Id");

      if (!teamId) {
        return res(
          ctx.status(400),
          ctx.json({ message: "Missing X-Team-Id header" })
        );
      }

      const count = countsByTeamId[teamId] ?? 0;

      return res(
        ctx.status(200),
        ctx.json({
          items: [],
          count,
        })
      );
    }
  );

export const mockGetProfilesCountError = (): RestHandler =>
  rest.get(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles`,
    async (request, res, ctx) => {
      const teamId = request.headers.get("X-Team-Id");

      if (!teamId) {
        return res(
          ctx.status(400),
          ctx.json({ message: "Missing X-Team-Id header" })
        );
      }

      return res(
        ctx.status(500),
        ctx.json({ message: "Failed to load profile count" })
      );
    }
  );

export const mockGetProfiles = (profiles: Profile[]): RestHandler =>
  rest.get(
    createUrl(`${config.COMPLIANCE_API_URL}/${apiVersion}/profiles`),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = Number.parseInt(searchParams.get("page") ?? "1");

      const pageSize = Number.parseInt(searchParams.get("limit") ?? "1");
      const searchTerm = searchParams.get("searchTerm") ?? "";
      let profileList = profiles;
      if (searchTerm)
        profileList = profiles.filter(
          (p) =>
            p.name?.toLowerCase().startsWith(searchTerm.toLowerCase()) ||
            p.internalId?.toLowerCase().startsWith(searchTerm.toLowerCase())
        );

      return res(
        ctx.status(200),
        ctx.json({
          items: Array.prototype.slice.call(
            profileList,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: profileList.length,
        })
      );
    }
  );

export const mockGetActiveUsers = (users: User[]): RestHandler =>
  rest.get(
    createUrl(`${config.COMPLIANCE_API_URL}/${apiVersion}/users/active`, true),
    async (_, res, ctx) => res(ctx.status(200), ctx.json(users))
  );

export const mockDeleteProfile = (): RestHandler =>
  rest.delete(
    createUrl(`${config.COMPLIANCE_API_URL}/${apiVersion}/profiles`),
    async (_, res, ctx) => res(ctx.status(200), ctx.json({}))
  );

export const mockAssignProfilesToUser = (): RestHandler =>
  rest.patch(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/assign/bulk`
    ),
    async (_, res, ctx) => res(ctx.status(200))
  );
export const mockGetProfileById = (
  profile?: Profile,
  profileId = "10"
): RestHandler =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}`
    ),
    async (_, res, ctx) => res(ctx.status(200), ctx.json(profile ?? {}))
  );

export const mockGetProfileTypes = (): RestHandler =>
  rest.get(
    createUrl(`${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/types`),
    async (_, res, ctx) => res(ctx.status(200), ctx.json(profileTypes))
  );

export const mocksaveAddressForProfile = (
  profileId = ":profileId"
): RestHandler =>
  rest.post(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/details/addresses`
    ),
    async (_, res, ctx) => {
      return res(
        ctx.status(200),
        ctx.json({
          id: "9183ba23-eb5d-4a08-a993-7f6192d21541",
          buildingDetails: "Test",
          street: "Test",
          city: "Test",
          region: "Test",
          postalCode: "Test",
          countryCode: "CA",
          type: "other",
          description: "Test",
        })
      );
    }
  );

export const mockCreateProfile = (
  spy?: jest.Mock,
  returnError?: boolean
): RestHandler =>
  rest.post(
    createUrl(`${config.COMPLIANCE_API_URL}/${apiVersion}/profiles`),
    async (_, res, ctx) => {
      if (spy) {
        spy({});
      }

      if (returnError) {
        return res(
          ctx.status(500),
          ctx.json({ error: "Profile creation failed" })
        );
      }

      return res(ctx.status(200), ctx.json({ id: "profile-123" }));
    }
  );

export const mockeditAddressForProfile = (
  profileId = ":profileId",
  addressId = ":addressId"
): RestHandler =>
  rest.put(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/details/addresses/${addressId}`
    ),
    async (_, res, ctx) => {
      return res(
        ctx.status(200),
        ctx.json({
          id: "9183ba23-eb5d-4a08-a993-7f6192d21541",
          buildingDetails: "Test",
          street: "Test",
          city: "Test",
          region: "Test",
          postalCode: "Test",
          countryCode: "CA",
          type: "other",
          description: "Test",
        })
      );
    }
  );

export const mockUseGetCurrencyCode = (): RestHandler =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/lookup/currencyCodes`
    ),
    async (_, res, ctx) =>
      res(
        ctx.status(200),
        ctx.json([
          "eur",
          "gbp",
          "usd",
          "cny",
          "cad",
          "aud",
          "nzd",
          "inr",
          "jpy",
          "chf",
          "zar",
          "dkk",
          "sek",
          "nok",
        ])
      )
  );

export const mockGetProfileDetails = (
  profileDetails?: ProfileDetails
): RestHandler =>
  rest.get(
    createUrl(`${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/10/details`),
    async (_, res, ctx) => res(ctx.status(200), ctx.json(profileDetails ?? {}))
  );

export const mockUpdateProfileDetails = (
  profileDetails?: ProfileDetails,
  profileId = "10",
  onRequest?: (_body: UpdateProfileDetailsPayload) => void
): RestHandler =>
  rest.put(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/details`
    ),
    async (req, res, ctx) => {
      if (onRequest) {
        onRequest(await req.json());
      }
      return res(ctx.status(200), ctx.json(profileDetails ?? {}));
    }
  );

export const mockGetProfileAddresses = (addresses: Addresses[]): RestHandler =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/10/details/addresses`
    ),
    async (_, res, ctx) => res(ctx.status(200), ctx.json(addresses ?? {}))
  );

export const mockUseGetCountryCode = (): RestHandler =>
  rest.get(
    createUrl(`${config.COMPLIANCE_API_URL}/${apiVersion}/lookup/countryCodes`),
    async (_, res, ctx) =>
      res(
        ctx.status(200),
        ctx.json([
          "af",
          "ax",
          "al",
          "dz",
          "as",
          "ad",
          "ao",
          "ai",
          "aq",
          "uz",
          "us",
          "ru",
          "ca",
        ])
      )
  );

export const mockAddToMonitoring = (): RestHandler =>
  rest.post(
    createUrl(`${config.COMPLIANCE_API_URL}/${apiVersion}/schedules`),
    async (_, res, ctx) => res(ctx.status(200))
  );

export const mockCreateExportProfile = (): RestHandler => {
  return rest.post(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/business/import`
    ),
    async (_, res, ctx) => res(ctx.status(200))
  );
};

export const mockGetAmlSearches = (
  type: string,
  searchDetails: AmlSearchByIdDetails,
  profileId = "12345"
): RestHandler =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/${type}/${profileId}`
    ),
    async (_, res, ctx) => {
      return res(ctx.status(200), ctx.json(searchDetails));
    }
  );

export const mockGetMonitoringSchedules = (
  monitoringDetails: AmlScreenMonitoringDetails
): RestHandler =>
  rest.get(
    createUrl(`${config.COMPLIANCE_API_URL}/${apiVersion}/schedules/12345`),
    async (_, res, ctx) => {
      return res(ctx.status(200), ctx.json(monitoringDetails));
    }
  );

export const mockUpdateMonitoring = (
  monitoringDetails: AmlScreenMonitoringDetails
): RestHandler =>
  rest.put(
    createUrl(`${config.COMPLIANCE_API_URL}/${apiVersion}/schedules/12345`),
    async (_, res, ctx) => res(ctx.status(200), ctx.json(monitoringDetails))
  );

export const mockDeleteFromMonitoring = (): RestHandler =>
  rest.delete(
    createUrl(`${config.COMPLIANCE_API_URL}/${apiVersion}/schedules/12345`),
    async (_, res, ctx) => res(ctx.status(200), ctx.json({}))
  );

export const mockAddNewKeyPartyBulk = (): RestHandler =>
  rest.post(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/12345/keyparties/bulk`
    ),
    async (_, res, ctx) => res(ctx.status(200))
  );

export const mockAddNewKeyParty = (profileId: string): RestHandler =>
  rest.post(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/keyparties`
    ),
    async (_, res, ctx) => res(ctx.status(200))
  );

export const mockSaveAMLSearchHitNote = (): RestHandler =>
  rest.post(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/hit/notes/123`
    ),
    async (_, res, ctx) => {
      return res(ctx.status(200));
    }
  );

export const mockGetUboDetails = (
  uboDetails: UboDetails[],
  profileId = ":profileId"
): RestHandler =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/ubo`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = Number.parseInt(searchParams.get("page") ?? "1");

      const pageSize = Number.parseInt(searchParams.get("limit") ?? "10");

      return res(
        ctx.status(200),
        ctx.json({
          items: Array.prototype.slice.call(
            uboDetails,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: uboDetails.length,
        })
      );
    }
  );
export const mockGetKeyPartiesByProfile = (
  profileId: string,
  data: KeyPartiesType[]
): RestHandler =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/keyparties`
    ),
    async (_, res, ctx) => {
      return res(
        ctx.status(200),
        ctx.json({
          count: data.length,
          items: data,
          apiFailed: false,
        })
      );
    }
  );

export const mockUpdateKeyParty = (
  keyPartyDetails: KeyPartiesType,
  profileId = "123456789",
  keyPartyId = "123456789"
): RestHandler =>
  rest.put(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/keyparties/${keyPartyId}`
    ),
    async (_, res, ctx) => res(ctx.status(200), ctx.json(keyPartyDetails))
  );

export const mockDeleteKeyParty = (
  profileId = "123456789",
  keyPartyId = "123456789"
): RestHandler =>
  rest.delete(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/keyparties/${keyPartyId}`
    ),
    async (_, res, ctx) => res(ctx.status(200), ctx.json({}))
  );

export const mockPostProfileToKycToMonitoring = (): RestHandler =>
  rest.post(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/kycMonitoring/profiles/bulk`
    ),
    async (_, res, ctx) =>
      res(ctx.status(200), ctx.json({ successful: ["10"] }))
  );

export const mockRemoveProfileFromKYCMonitoring = (): RestHandler =>
  rest.delete(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/kycMonitoring/profiles`
    ),
    async (_, res, ctx) =>
      res(ctx.status(200), ctx.json({ successful: ["10"] }))
  );

export const mockGetNotesByProfile = (
  profileId: string,
  data: MockGetNotesResponse,
  apiFailed = false
): RestHandler =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/notes`
    ),
    async (_, res, ctx) => {
      return res(
        apiFailed ? ctx.status(500) : ctx.status(200),
        apiFailed
          ? ctx.json({ apiFailed: true, count: 0, items: [] })
          : ctx.json({
              count: data.count,
              items: data.items,
              apiFailed,
            })
      );
    }
  );

export const mockCreateProfileNote = (profileId: string): RestHandler =>
  rest.post(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/notes`
    ),
    async (_, res, ctx) => {
      return res(ctx.status(200));
    }
  );

export const mockGetMonitoringScheduleById = (
  profileId: string,
  scheduleId: string,
  scheduleDetails: AmlScreenMonitoringScheduleDetails
): RestHandler =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/schedules/${scheduleId}`
    ),
    async (_, res, ctx) => {
      return res(ctx.status(200), ctx.json(scheduleDetails));
    }
  );

export const mockDeleteAmlSearch = (profileId: string): RestHandler =>
  rest.delete(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/searches`
    ),
    async (_, res, ctx) => res(ctx.status(200), ctx.json({}))
  );

export const mockDeleteAttachmentsFromProfile = async (
  profileId: string,
  attachmentId: string,
  statusCode = 200
): Promise<RestHandler> =>
  rest.delete(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/attachments/${attachmentId}`
    ),
    async (_, res, ctx) => {
      if (statusCode >= 400) {
        return res(
          ctx.status(statusCode),
          ctx.json({
            title: "Error",
            status: statusCode,
            detail: "Failed to delete attachment",
          })
        );
      }
      return res(ctx.status(200), ctx.json({ success: true }));
    }
  );

export const mockDownloadAttachment = (
  profileId: string,
  attachmentId: string
): RestHandler =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/attachments/${attachmentId}/download`
    ),
    async (_, res, ctx) => {
      return res(
        ctx.status(200),
        ctx.json({
          downloadUrl: "https://www.google.com",
          fileName: "test.pdf",
        })
      );
    }
  );

export const mockUpdateAttachments = (
  profileId: string,
  attachmentId: string,
  statusCode = 200
): RestHandler =>
  rest.put(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/attachments/${attachmentId}`
    ),
    async (_, res, ctx) => {
      if (statusCode >= 400) {
        return res(
          ctx.status(statusCode),
          ctx.json({
            title: "Error",
            status: statusCode,
            detail: "Failed to update attachment",
          })
        );
      }
      return res(
        ctx.status(200),
        ctx.json({
          profileId: "123",
          id: 321,
          fileName: "test.pdf",
          fileSizeInBytes: "123",
          documentType: "pdf",
        })
      );
    }
  );

export const mockGetAttachments = (
  profileId: string,
  attachments: Attachments[] = []
): RestHandler =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/attachments`
    ),
    async (_, res, ctx) =>
      res(
        ctx.status(200),
        ctx.json({
          items: attachments,
          count: attachments.length,
          totalSize: attachments.length,
        })
      )
  );

export const mockCreateExportProfileConnectEndPoint = (
  companyId: string,
  spy?: jest.Mock,
  options?: {
    response?: { id?: string; profileUrl?: string };
    headerSpy?: jest.Mock;
  }
): RestHandler =>
  rest.post(
    createUrl(
      `${config.UPP_CONNECT_API_URL}/${apiVersion}/compliance/kyc-protect/profiles/import/companies/${companyId}`
    ),
    async (req, res, ctx) => {
      if (spy) spy(await req.json());
      if (options?.headerSpy) {
        options.headerSpy(req.headers.get("X-Team-Id"));
      }
      return res(
        ctx.status(200),
        ctx.json(
          options?.response ?? {}
        )
      );
    }
  );

export const mockGetProfileReportById = (
  profileId: string,
  reportStatus: ReportsStatus = ReportsStatus.Completed
): RestHandler =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/Profiles/${profileId}/reports`,
      true
    ),
    async (_, res, ctx) => {
      const mockReportsList: ReportsListItem[] = [
        {
          id: "69f9cf95-470f-4cc4-b7c1-0ff3ee644c12",
          type: "Type1",
          fileName: "report1.pdf",
          status: reportStatus,
          createdByUser: "User1",
          requestedDate: "2023-01-01T00:00:00Z",
          createdAt: "2023-01-01T00:00:00Z",
          expiresAt: "2023-12-31T23:59:59Z",
          criteria: {
            fromDate: "2023-01-01T00:00:00Z",
            toDate: "2023-12-31T23:59:59Z",
          },
          open: true,
          orderedBy: "User1",
          expiryDate: "2023-12-31T23:59:59Z",
        },
      ];
      return res(ctx.status(200), ctx.json(mockReportsList));
    }
  );

export const mockPatchAmlSearch = (
  searchType: "businesses" | "individuals",
  returnError = false
): RestHandler =>
  rest.patch(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/${searchType}/:searchId`,
    async (_request, res, ctx) => {
      if (returnError) {
        return res(ctx.status(500), ctx.json({ message: "Update failed" }));
      }

      return res(ctx.status(200), ctx.json({}));
    }
  );
